/**
 * City street drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawings`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const cityStreetDrawingsPack: ToilePack = {
    slug: 'city-street-drawings',
    category: 'illustration',
    name: 'City street drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'paris', label: 'Paris street', subject: 'a narrow Paris street of stone buildings with iron balconies, a café awning and cobbles receding to a corner'},
        {key: 'alley', label: 'Tokyo alley', subject: 'a narrow Tokyo alley with blank paper lanterns, tangled overhead wires and a bicycle against a wall'},
        {key: 'rain', label: 'Rainy crossing', subject: 'a city crossing in the rain, open umbrellas, reflections on the wet road and a tram passing'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil', style: 'graphite pencil drawing, soft layered shading from sharp 2H detail to deep 6B darks, visible paper tooth', palette: 'graphite gray only', framing: 'on plain white paper, the whole sheet used, a thin white margin around the drawing'},
        {key: 'ink', label: 'Pen and ink', style: 'pen and ink drawing, confident contour lines, fine crosshatching and stippling for tone', palette: 'black ink only', framing: 'on clean white paper, centered, a white margin all round'},
        {key: 'charcoal', label: 'Charcoal', style: 'charcoal drawing, smudged tonal masses with crisp accents and lifted highlights', palette: 'black charcoal on white', framing: 'on white drawing paper, the subject filling the sheet, a white edge left clear'},
    ],
    directions: ['Venice', 'night', 'a market'],
    directionHint: 'try: Venice',
    seo: {
        title: 'City Street Drawings: Paris, Tokyo Alley, Rainy Crossing',
        metaDescription: 'Nine city street drawings: a Paris street, a lantern-lit Tokyo alley and a rainy tram crossing, each in pencil, pen and ink, or charcoal.',
        h1: 'City street drawings: a Paris street, a Tokyo alley, a rainy crossing',
        tagline: 'Three city streets, each drawn in graphite pencil, pen and ink, and charcoal.',
        intro: 'Three city streets: a narrow Paris street with iron balconies, a Tokyo alley with paper lanterns and tangled wires, and a rainy crossing with umbrellas and a tram. Each is drawn in pencil, pen and ink, and charcoal, so perspective and wet ground compare across the media. Type a different city or a twist, such as Venice or night, and the same media carry it.',
        useCases: [
            'a perspective practice sheet',
            'a framed print for a city apartment',
            'a reference for balconies and wires',
            'a sketchbook page for urban sketching',
        ],
        faq: [
            {q: 'What is in this pack?', a: 'Nine drawings of three streets: a Paris street with balconies and a cafe awning, a Tokyo alley with lanterns and wires, and a rainy crossing with a tram. Each appears in three media.'},
            {q: 'Can I ask for a different city?', a: 'Yes. Type a city or twist, such as Venice, night or a market. A typed direction is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Pencil shades from sharp detail to deep darks on plain white paper. Pen and ink uses contour lines and crosshatching. Charcoal uses smudged masses with crisp accents and lifted highlights.'},
            {q: 'Is each drawing wide or tall?', a: 'Tall. The city street drawings are taller than they are wide.'},
        ],
    },
}
